// @flow
import React, { Component } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { Toast as GestaltToast } from 'gestalt';

type Props = {|
  color?: *, // infer from Gestalt union type for Toast (props.color)
  icon?: *, // infer from Gestalt union type for Icon (props.icon)
  imageUrl?: string,
  href?: string,
  onClick?: (SyntheticEvent<>) => void,
  onHide: () => void,
  text: string | string[],
  duration?: number,
  openNewPage?: boolean, // used when href is specified to determine the `target` property of <a>
|};

type State = {|
  visible: boolean,
  hiding: boolean,
|};

const DEFAULT_TOAST_VISIBLE_TIME = 2000;
function textValidator(props: Object, propName: string, componentName: string) {
  if (props[propName]) {
    const value = props[propName];
    if (value instanceof Array) {
      return value.length <= 2
        ? null
        : new Error(propName + ' in ' + componentName + ' should contain a max of 2 strings');
    }
  }

  // assume all ok
  return null;
}

export default class Toast extends Component<Props, State> {
  static propTypes = {
    color: PropTypes.oneOf(['orange', 'red', 'darkGray']),
    icon: PropTypes.string,
    imageUrl: PropTypes.string,
    href: PropTypes.string,
    onClick: PropTypes.func,
    onHide: PropTypes.func.isRequired,
    text: textValidator,
    duration: PropTypes.number,
    openNewPage: PropTypes.bool, // used when href is specified to determine the `target` property of <a>
  };

  static defaultProps = {
    duration: DEFAULT_TOAST_VISIBLE_TIME,
  };

  state: State = {
    hiding: false,
    visible: false,
  };

  componentDidMount() {
    setTimeout(() => {
      this.setState({ visible: true });
    }, 100); // Wait 100ms to allow rendering engine to paint
    this.timeout = setTimeout(this.handleHide, this.props.duration);
  }

  componentWillUnmount() {
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
  }

  handleHide = () => {
    const HIDE_DURATION = 200; // Keep in sync with ToastBase
    this.setState({ hiding: true });
    this.timeout = setTimeout(this.props.onHide, HIDE_DURATION);
  };

  handleMouseEnter = () => {
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
  };

  handleMouseLeave = () => {
    this.timeout = setTimeout(this.handleHide, this.props.duration);
  };

  timeout: ?TimeoutID;

  render() {
    const { icon, imageUrl, href, onClick, text, openNewPage } = this.props;
    const color = this.props.color ? this.props.color : 'darkGray';

    const thumbnailAltText = typeof text === 'string' ? text : `${text[0]} ${text[1]}`;

    let iconName = icon;
    if (href) {
      iconName = !imageUrl ? 'arrow-circle-forward' : undefined;
    }

    const thumbnail =
      href || imageUrl ? (
        <div className="imageWrapper">
          <img className="toastImage" src={imageUrl} alt={thumbnailAltText} />
        </div>
      ) : (
        undefined
      );

    const toast = <GestaltToast color={color} icon={iconName} text={text} thumbnail={thumbnail} />;

    return (
      <div
        className={cx(
          'ToastBase',
          'react',
          { visible: this.state.visible },
          { hiding: this.state.hiding },
        )}
        onMouseEnter={this.handleMouseEnter}
        onMouseLeave={this.handleMouseLeave}
        data-test-id="toast"
      >
        {href ? (
          <a
            href={href}
            onClick={onClick}
            target={openNewPage ? '_blank' : null}
            rel={openNewPage ? 'noopener noreferrer' : null}
          >
            {toast}
          </a>
        ) : (
          toast
        )}
      </div>
    );
  }
}
